import { useEffect, useState } from 'react';

import { TextLink, TextLinkSize } from '../../../components/TextLink';
import TextField from '../../../components/TextField';
import { ValidationAnnouncement } from '../../../components/ValidationAnnouncement';
import { useValidationAnnouncement } from '../../../hooks/useValidationAnnouncement';

export type ApprovalEditMode = 'editing' | 'idle' | 'saved';

export interface ApprovalEditState {
  draftReason: string;
  mode: ApprovalEditMode;
  savedReason: string;
}

interface ApprovalDetailsEditableRowProps {
  editState?: ApprovalEditState;
  id: string;
  isDocumentRow?: boolean;
  isEditable?: boolean;
  onCancel: (rowId: string) => void;
  onEdit: (rowId: string) => void;
  onReasonChange: (rowId: string, value: string) => void;
  onRemove: (rowId: string) => void;
  onRequestEdit: (rowId: string) => void;
  onSave: (rowId: string) => void;
  summary: React.ReactNode;
}

const UPDATE_REASON_LABEL = 'Enter a brief and clear reason describing the update.';
const UPDATE_REASON_TOOLTIP = 'Explain what needs to be updated in this field. What is missing or incorrect?';
const SAVED_REASON_LABEL = 'Reason for Update:';
const MINIMUM_UPDATE_REASON_LENGTH = 3;

function ApprovalDetailsEditableRow({
  editState,
  id,
  isDocumentRow = false,
  isEditable = true,
  onCancel,
  onEdit,
  onReasonChange,
  onRemove,
  onRequestEdit,
  onSave,
  summary,
}: ApprovalDetailsEditableRowProps) {
  const mode = editState?.mode ?? 'idle';
  const savedReason = editState?.savedReason ?? '';
  const draftReason = editState?.draftReason ?? '';
  const currentReason = mode === 'saved' ? savedReason : draftReason;
  const baseTestId = `approval-details-editable-row-${id}`;
  const [showError, setShowError] = useState(false);

  const trimmedDraftReason = draftReason.trim();
  let fieldErrorMessage: string | undefined;

  if (showError) {
    if (trimmedDraftReason.length === 0) {
      fieldErrorMessage = 'A reason for the update is required.';
    } else if (trimmedDraftReason.length < MINIMUM_UPDATE_REASON_LENGTH) {
      fieldErrorMessage = `Must be at least ${MINIMUM_UPDATE_REASON_LENGTH} characters.`;
    }
  }

  // ACP-2092 (Section 508): Save Changes with no (or too short a) reason rendered the
  // message on the field and said nothing — focus stayed on the link. Announce it and
  // move focus to the reason, scoped to this row so other rows are not swept in.
  const {
    announcement: errorAnnouncement,
    reportInvalidFields,
    scopeRef,
  } = useValidationAnnouncement<HTMLDivElement>({ errorSignature: fieldErrorMessage ? 'reason' : '' });

  const handleSaveClick = () => {
    if (trimmedDraftReason.length < MINIMUM_UPDATE_REASON_LENGTH) {
      setShowError(true);
      reportInvalidFields();

      return;
    }
    setShowError(false);
    onSave(id);
  };

  useEffect(() => {
    if (mode === 'editing') {
      setShowError(false);
    }
  }, [mode]);

  const renderActions = () => {
    if (!isEditable) {
      return null;
    }

    if (mode === 'idle') {
      return (
        <TextLink
          className='approval-details-editable-row__action-link'
          data-testid={`${baseTestId}-request-edit`}
          size={TextLinkSize.LARGE}
          text='Request Edit'
          onClick={() => onRequestEdit(id)}
        />
      );
    }

    if (mode === 'editing') {
      return (
        <div
          className='approval-details-editable-row__action-group'
          data-testid={`${baseTestId}-editing-actions`}>
          <TextLink
            className='approval-details-editable-row__action-link'
            data-testid={`${baseTestId}-cancel`}
            size={TextLinkSize.LARGE}
            text='Cancel'
            onClick={() => onCancel(id)}
          />
          <span
            aria-hidden='true'
            className='approval-details-editable-row__action-separator'>
            |
          </span>
          <TextLink
            className='approval-details-editable-row__action-link'
            data-testid={`${baseTestId}-save`}
            size={TextLinkSize.LARGE}
            text='Save Changes'
            onClick={handleSaveClick}
          />
        </div>
      );
    }

    return (
      <div
        className='approval-details-editable-row__action-group'
        data-testid={`${baseTestId}-saved-actions`}>
        <TextLink
          className='approval-details-editable-row__action-link'
          data-testid={`${baseTestId}-edit`}
          size={TextLinkSize.LARGE}
          text='Edit'
          onClick={() => onEdit(id)}
        />
        <span
          aria-hidden='true'
          className='approval-details-editable-row__action-separator'>
          |
        </span>
        <TextLink
          className='approval-details-editable-row__action-link'
          data-testid={`${baseTestId}-remove`}
          size={TextLinkSize.LARGE}
          text='Remove'
          onClick={() => onRemove(id)}
        />
      </div>
    );
  };

  const renderEditor = () => {
    if (!isEditable) {
      return null;
    }

    if (mode === 'idle') {
      return null;
    }

    return (
      <div
        className='approval-details-editable-row__editor'
        data-testid={`${baseTestId}-editor`}>
        {mode === 'editing' ? (
          <TextField
            multiline
            required
            data-testid={baseTestId}
            errorMessage={fieldErrorMessage}
            label={UPDATE_REASON_LABEL}
            maxLength={255}
            name={`${baseTestId}-reason`}
            rows={4}
            tooltipText={UPDATE_REASON_TOOLTIP}
            value={currentReason}
            onChange={(event) => onReasonChange(id, event.target.value)}
          />
        ) : (
          <div
            className='approval-details-editable-row__saved-reason'
            data-testid={`${baseTestId}-saved-reason`}>
            <span
              className='approval-details-editable-row__saved-reason-label'
              data-testid={`${baseTestId}-saved-reason-label`}>
              {SAVED_REASON_LABEL}{' '}
            </span>
            <span
              className='approval-details-editable-row__saved-reason-text'
              data-testid={`${baseTestId}-saved-reason-text`}>
              {currentReason}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      ref={scopeRef}
      className={`approval-details-editable-row${isDocumentRow ? ' approval-details-editable-row--document' : ''}`}
      data-testid={baseTestId}>
      <ValidationAnnouncement
        data-testid={`${baseTestId}-error-announcement`}
        message={errorAnnouncement}
      />
      <div
        className='approval-details-editable-row__summary'
        data-testid={`${baseTestId}-summary`}>
        <div
          className='approval-details-editable-row__summary-content'
          data-testid={`${baseTestId}-summary-content`}>
          {summary}
        </div>
        <div
          className='approval-details-editable-row__actions'
          data-testid={`${baseTestId}-actions`}>
          {renderActions()}
        </div>
      </div>

      {renderEditor()}
    </div>
  );
}

export default ApprovalDetailsEditableRow;
